'use client';

import { useEffect } from 'react';
import {
  ELEVAR_EVENT_ORDER,
  ELEVAR_VIEWPORT_TRACKING,
} from './elevar-constants';
import { useViewportTracking } from './use-viewport-tracking';

export function useProductGridViewportTracking({
  listName,
  products,
  batchSize = ELEVAR_VIEWPORT_TRACKING.BATCH_SIZE,
  batchDelay = ELEVAR_VIEWPORT_TRACKING.BATCH_DELAY,
}: {
  listName: string;
  products: unknown[] | undefined;
  batchSize?: number;
  batchDelay?: number;
}) {
  const { observe, unobserve, forceFlush } = useViewportTracking({
    listName,
    batchSize,
    batchDelay,
  });

  // Set up viewport tracking for product cards when they mount
  useEffect(() => {
    // Add delay to ensure dl_user_data fires first on page refresh
    const timeoutId = setTimeout(() => {
      const productCards = document.querySelectorAll(
        '[data-elevar-impression]'
      );
      for (const card of productCards) {
        observe(card);
      }
    }, ELEVAR_EVENT_ORDER.USER_DATA_DELAY); // Ensure dl_user_data fires first

    return () => {
      clearTimeout(timeoutId);
      const productCards = document.querySelectorAll(
        '[data-elevar-impression]'
      );
      for (const card of productCards) {
        unobserve(card);
      }
    };
  }, [observe, unobserve]);

  // Re-track when products change (pagination, filters, etc.)
  useEffect(() => {
    if (!products?.length) return;

    const timeoutId = setTimeout(() => {
      const productCards = document.querySelectorAll(
        '[data-elevar-impression]'
      );
      for (const card of productCards) {
        observe(card);
      }

      // Force flush after products are loaded to catch any missed impressions
      forceFlush();
    }, 100); // Slightly longer delay for DOM updates

    return () => clearTimeout(timeoutId);
  }, [products, observe, forceFlush]);
}
